import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Target, Sparkles, TrendingUp, Settings2, Loader2, AlertTriangle } from 'lucide-react';
import { projectMonthly } from '../../utils/monthlyProjection';
import { PlanReturnView, periodRangeLabel, rateOf } from '../../utils/transferReturns';
import { periodWithRange } from './projectionLegend';
import { fmtNum, fmtEur, estimateTaxSaving } from '../../utils/formatArcaNumbers';
import {
	percentageToMonthlyEur,
	snapPercentageToGrid,
	floorPercentageToGrid,
	ceilPercentageToGrid,
	CONTRIBUTION_PERCENTAGE_STEP
} from '../../utils/contributionPercentage';
import { EmployeeContributionTypeEnum } from '../../types/enums';

export interface ProjectionSimulatorV2Props {
	currentSaved: number;
	monthlyOwn: number;
	monthlyCompany: number;
	/** Años hasta los 67 (con la edad del usuario). Sin edad, se proyecta a 30 años y se dice así. */
	yearsToRetirement?: number;
	maxMonthly?: number;
	minMonthly?: number;
	/**
	 * Rentabilidad de comparsa del plan de empleo de su empresa (`/plan-returns/arca-plans`,
	 * chirigota#576). `undefined` mientras llega; `null` sin datos: entonces no se proyecta.
	 */
	planReturn?: PlanReturnView | null;
	onContributionChange?: (newAmount: number) => void;
	onConfigureClick?: () => void;
	saving?: boolean;
	translate?: (key: string) => string;
	employeeContributionType?: string;
	rawPercentageContribution?: number;
	/** Mínimo en % (solo modo PERCENTAGE). Ej: 1 para IDFinance. */
	percentageMin?: number;
	/** Máximo en % (solo modo PERCENTAGE), derivado del cap en euros. */
	percentageMax?: number;
	/** Salario bruto anual — necesario para mostrar los euros equivalentes en modo %. */
	grossAnnualSalary?: number;
	isPayrollLocked?: boolean;
	contributionDayDisplay?: number;
}

/** Horizonte si no sabemos la edad del empleado. */
const DEFAULT_YEARS = 30;

const ProjectionSimulatorV2: React.FC<ProjectionSimulatorV2Props> = ({
	currentSaved,
	monthlyOwn,
	monthlyCompany,
	yearsToRetirement,
	maxMonthly = 500,
	minMonthly = 0,
	planReturn,
	onContributionChange,
	onConfigureClick,
	saving,
	translate: t,
	employeeContributionType,
	rawPercentageContribution,
	percentageMin = 0,
	percentageMax = 0,
	grossAnnualSalary = 0,
	isPayrollLocked,
	contributionDayDisplay
}) => {
	const translate = t || ((key: string) => key);

	// En empresas PERCENTAGE el slider opera en su unidad nativa (%), igual que el modal.
	// Lo que se persiste es el % directo, que NO depende del salario (el salario solo sirve
	// para mostrar el € de referencia). Por eso NO exigimos `grossAnnualSalary > 0`: si lo
	// hiciéramos, sin salario caería a modo euros y handleQuickSave guardaría el euro crudo
	// como % → mismo bug de Miguel, silencioso. Una empresa PERCENTAGE nunca cae a euros.
	const isPercentageMode = employeeContributionType === EmployeeContributionTypeEnum.PERCENTAGE;

	// Salario disponible: necesario para configurar una aportación en %.
	const hasSalary = grossAnnualSalary > 0;
	// Sin salario definido NO se puede configurar una aportación en % (el % es "% del
	// salario", sin salario no hay base sobre la que aplicarlo). Bloqueamos el cambio y
	// dirigimos a RRHH/responsable (el empleado no configura su propio salario). Nunca
	// caemos a euros. Aplica con o sin mínimo.
	const blockMissingSalary = isPercentageMode && !hasSalary;

	// Límites y paso del slider según el modo.
	// ⚠️ Number(): los valores de config del backend (employeeContributionMinimum) pueden
	// llegar como STRING. Radix Slider, con un `min` string, hace concatenación en su
	// cálculo interno (min + ratio*(max-min)) → la bolita se dispara al máximo y no se
	// puede arrastrar. Forzamos número SIEMPRE.
	// Los EXTREMOS también van a la rejilla, aquí y no solo en el padre: al arrastrar hasta
	// el tope se persiste exactamente este número. El máximo hacia ABAJO (nunca por encima
	// del techo legal) y el mínimo hacia ARRIBA (nunca por debajo del mínimo de la empresa).
	const sliderMin = isPercentageMode
		? ceilPercentageToGrid(Number(percentageMin) || 0)
		: Number(minMonthly) || 0;
	const sliderMax = isPercentageMode
		? floorPercentageToGrid(Number(percentageMax) || 0)
		: Number(maxMonthly) || 0;
	const sliderStep = isPercentageMode ? CONTRIBUTION_PERCENTAGE_STEP : 10;

	// En modo % el valor se lleva a la rejilla ANTES de acotarlo, para que el snap no pueda
	// empujarlo por encima del techo. Un % fuera de la rejilla no es mostrable con 2
	// decimales y hace que el porcentaje que se ve y el euro que se cobra dejen de cuadrar.
	const clampToRange = (v: number) => {
		const onGrid = isPercentageMode ? snapPercentageToGrid(v) : v;
		return Math.min(Math.max(onGrid, sliderMin), Math.max(sliderMin, sliderMax));
	};

	// Slider controlado IGUAL que el del modal (EditContributionModalV2): el `value` es
	// el estado y `onValueChange` lo escribe directo. Solo acotamos al INICIALIZAR (datos
	// corruptos) y al GUARDAR; durante el arrastre Radix ya respeta min/max/step.
	const [sliderValue, setSliderValue] = useState(() => {
		const initial = isPercentageMode
			? Math.max(rawPercentageContribution ?? 0, percentageMin)
			: Math.max(monthlyOwn, minMonthly);
		return Number.isFinite(initial) ? clampToRange(initial) : sliderMin;
	});

	// El valor que llega a Radix tiene que ser SIEMPRE finito. Si por un glitch de medición
	// (la tarjeta reflota al cambiar la proyección mientras se arrastra) Radix emitiera NaN
	// y se colara en el estado, el slider se quedaba "loco": bolita descolocada y el número
	// marcando "0,00%" (fmtNum devuelve "0,00" para NaN). Con este guard nunca pasa NaN al
	// control y, si ocurriera, se recupera al último valor válido (o al mínimo).
	const safeSliderValue = Number.isFinite(sliderValue) ? sliderValue : sliderMin;

	// El slider se mueve en % o en €; los cálculos (proyección, ahorro fiscal) siempre
	// necesitan euros mensuales → derivamos el equivalente al vuelo en modo %.
	const monthlyEurValue = isPercentageMode
		? percentageToMonthlyEur(safeSliderValue, grossAnnualSalary)
		: safeSliderValue;

	// El % guardado, ya en la rejilla. Los datos anteriores al fix traen decimales de sobra
	// (1,505 %) y esos decimales no son mostrables: al pintarlos se redondean a 1,51 % y
	// dejan de cuadrar con su propio importe en euros. Se muestra y se compara alineado.
	const storedPercentage = snapPercentageToGrid(rawPercentageContribution ?? 0);

	// Valor de referencia para detectar cambios: el % guardado en modo %, los € en €.
	// Va alineado porque el slider arranca en el valor de la rejilla; si no, el botón de
	// guardar aparecería activo solo con abrir la pantalla, sin que el usuario toque nada.
	const baselineValue = isPercentageMode ? storedPercentage : monthlyOwn;

	// Re-acotar el estado SOLO cuando cambian los LÍMITES (carga de datos / refetch). NO
	// corre durante el arrastre (sliderMin/sliderMax son estables mientras se arrastra), así
	// que no interfiere con Radix; solo corrige si el mínimo sube tras cargar/refrescar.
	useEffect(() => {
		setSliderValue((prev) => Math.min(Math.max(prev, sliderMin), Math.max(sliderMin, sliderMax)));
	}, [sliderMin, sliderMax]);

	// Horizonte: hasta los 67 con su edad; sin ella, 30 años (y el título lo dice).
	const knowsAge = yearsToRetirement !== undefined;
	const years = knowsAge ? Math.max(0, Math.round(yearsToRetirement)) : DEFAULT_YEARS;

	// Mes a mes (aportación a final de mes) con la tasa mensual NETA de comparsa, como el resto
	// de canales. Sin rentabilidad no se proyecta (nunca una tasa por defecto).
	const project = (monthly: number) =>
		planReturn ? projectMonthly(currentSaved, monthly, rateOf(planReturn), years) : 0;
	const projectionCurrent = useMemo(
		() => project(monthlyOwn + monthlyCompany),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentSaved, monthlyOwn, monthlyCompany, years, planReturn]
	);
	const projectionNew = useMemo(
		() => project(monthlyEurValue + monthlyCompany),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentSaved, monthlyEurValue, monthlyCompany, years, planReturn]
	);

	const projectionDiff = projectionNew - projectionCurrent;
	const taxSaving = estimateTaxSaving(monthlyEurValue);
	const hasChanged = safeSliderValue !== baselineValue;

	return (
		/*
		 * 🔴 DOS tarjetas separadas, no una partida por dentro.
		 *
		 * Antes era una sola `Card` con borde verde de 2 px dividida por un `border-r`. La pantalla
		 * de referencia que Elena montó en el Storybook de Proa (`MiBeneficio.stories.tsx`) son dos
		 * tarjetas independientes con `gap-4`: la del control en blanco y la de la proyección con
		 * fondo `bg-primary/5` y sin borde. Era la diferencia de layout más visible entre lo que se
		 * ve en dev y lo que ella dejó como referencia.
		 *
		 * ⚠️ La tarjeta del control NO usa todavía el `AmountStepCard` del registry, que es el
		 * componente que Proa creó justamente para que esta pantalla, `ActivePlanSection` y
		 * `RescueRentaSimulatorV2` dejaran de reimplementar el mismo patrón cada una. No se puede:
		 * su CTA y su enlace secundario no aceptan `onClick`, su `step` está fijo en 10 y su
		 * `helperText` es texto plano, así que no cubre ni el guardado ni el modo porcentaje ni el
		 * aviso de nómina cerrada. Usarlo exigiría modificarlo aquí, que es crear la misma deriva
		 * que se acaba de eliminar. Queda pendiente de hablarlo con Elena.
		 */
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
			{/* 🔴 `min-w-0` en las DOS tarjetas, que son los grid items.
			    El tamaño mínimo automático de un grid item es su `min-content`, así que sin esto una
			    columna puede negarse a encoger y estrechar a la otra. La del slider es la que no
			    puede estrecharse: si su track se queda con ancho ~0, Radix mide mal y emite NaN al
			    arrastrar.
			    Estaba en un `div` interno, donde es INERTE —un bloque en flujo normal ya está
			    limitado por su contenedor—, así que protegía sobre el papel y no en la práctica. */}
			<Card className="shadow-xs overflow-hidden min-w-0">
				<CardContent className="p-0">
					<div>
						<div className="p-6">
							<div className="flex items-center gap-2 mb-1">
								<Target className="w-5 h-5 text-primary" />
								<h2 className="text-lg font-semibold">
									{translate('benefit.simulator.howMuchContribute')}
								</h2>
							</div>
							<p className="text-xs text-gray-500 mb-4">
								{translate('benefit.simulator.currentContribution')}{' '}
								<span className="font-semibold text-gray-900">
									{isPercentageMode
										? `${fmtNum(storedPercentage, 2)}%${
												hasSalary && storedPercentage > 0
													? ` (${fmtEur(monthlyOwn)} ${translate('benefit.simulator.perMonth')})`
													: ''
											}`
										: `${fmtEur(monthlyOwn)} ${translate('benefit.simulator.perMonth')}`}
								</span>
							</p>

							<div className="mb-4">
								<div className="flex items-baseline justify-between mb-3">
									{isPercentageMode ? (
										<>
											<span className="text-4xl font-bold text-primary">
												{fmtNum(safeSliderValue, 2)}%{' '}
												{hasSalary && (
													<span className="text-lg font-medium text-gray-500">
														({fmtEur(monthlyEurValue)}
														{translate('benefit.simulator.perMonth')})
													</span>
												)}
											</span>
											<span className="text-sm text-gray-500">
												{translate('benefit.simulator.ofSalary')}
											</span>
										</>
									) : (
										<>
											<span className="text-4xl font-bold text-primary">
												{fmtNum(safeSliderValue, 0)}€
											</span>
											<span className="text-sm text-gray-500">
												{translate('benefit.simulator.perMonth')}
											</span>
										</>
									)}
								</div>
								<Slider
									value={[safeSliderValue]}
									onValueChange={(v) => {
										// Ignora valores no finitos por seguridad; el estado solo guarda números.
										if (Number.isFinite(v[0])) setSliderValue(v[0]);
									}}
									min={sliderMin}
									max={sliderMax}
									step={sliderStep}
									disabled={isPayrollLocked || blockMissingSalary}
									className={`**:[[role=slider]]:bg-primary [&_[data-slot=range]]:bg-primary${isPayrollLocked || blockMissingSalary ? ' opacity-50 cursor-not-allowed' : ''}`}
								/>
								<div className="flex justify-between text-xs text-gray-500 mt-2">
									{isPercentageMode ? (
										<>
											<span>Min: {fmtNum(percentageMin, 2)}%</span>
											<span className="font-medium text-primary">
												{translate('benefit.simulator.max')}: {fmtNum(percentageMax, 2)}%
											</span>
										</>
									) : (
										<>
											<span>{minMonthly > 0 ? `Min: ${fmtNum(minMonthly, 0)}€` : '0€'}</span>
											<span>{fmtNum(Math.round((minMonthly + maxMonthly) / 2), 0)}€</span>
											<span>{fmtNum(maxMonthly, 0)}€</span>
										</>
									)}
								</div>
							</div>

							{blockMissingSalary ? (
								<div
									className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"
									data-testid="missing-salary-message"
								>
									<AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
									<span>{translate('benefit.simulator.missingSalary')}</span>
								</div>
							) : isPayrollLocked ? (
								<div
									className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"
									data-testid="payroll-locked-message"
								>
									<AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
									<span>
										{translate('benefit.simulator.payrollLocked').replace(
											'{day}',
											String((contributionDayDisplay ?? 1) + 1)
										)}
									</span>
								</div>
							) : hasChanged ? (
								<Button
									onClick={() => onContributionChange?.(clampToRange(safeSliderValue))}
									disabled={saving}
									className="w-full bg-primary hover:bg-primary/90 text-white rounded-full gap-2"
								>
									{saving ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										<Sparkles className="w-4 h-4" />
									)}
									{saving
										? translate('benefit.simulator.saving')
										: isPercentageMode
											? translate('benefit.simulator.changeToPercentage').replace(
													'{amount}',
													fmtNum(safeSliderValue, 2)
												)
											: translate('benefit.simulator.changeTo').replace(
													'{amount}',
													String(safeSliderValue)
												)}
								</Button>
							) : (
								<Button
									onClick={onConfigureClick}
									className="w-full bg-primary hover:bg-primary/90 text-white rounded-full gap-2"
								>
									<Settings2 className="w-4 h-4" />
									{translate('benefit.simulator.configureContribution')}
								</Button>
							)}
							<button
								onClick={onConfigureClick}
								className="text-xs text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors mx-auto block mt-1"
							>
								{translate('benefit.simulator.viewBreakdown')}
							</button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* La proyección: tarjeta propia, sin borde y con fondo tintado, como en la referencia. */}
			<Card className="border-none shadow-xs bg-primary/5 min-w-0">
				<CardContent className="p-5 sm:p-6">
					<div className="min-w-0">
						{/* Versalitas espaciadas, no texto corrido: es lo que distingue una etiqueta de
						    sección de un párrafo cualquiera en el sistema. */}
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
							{knowsAge
								? translate('benefit.simulator.projectionAt67')
								: translate('benefit.simulator.projectionInYears').replace(
										'{years}',
										String(years)
									)}
						</p>
						{planReturn ? (
							<>
								<p className="text-3xl font-bold text-primary tracking-tight mb-1">
									{fmtNum(projectionNew, 0)}€
								</p>

								{projectionDiff > 0 && (
									<p className="text-sm font-medium text-primary flex items-center gap-1 mb-4">
										<TrendingUp className="w-4 h-4" />
										{translate('benefit.simulator.moreThanNow').replace(
											'{amount}',
											fmtNum(projectionDiff, 0)
										)}
									</p>
								)}
								{projectionDiff === 0 && (
									<p className="text-sm text-gray-500 mb-4">
										{translate('benefit.simulator.withCurrentContribution')}
									</p>
								)}
							</>
						) : (
							<p
								className="text-sm text-muted-foreground mb-4"
								data-testid="projection-unavailable"
							>
								{planReturn === undefined
									? translate('benefit.simulator.returnLoading')
									: translate('benefit.simulator.returnNoData')}
							</p>
						)}

						{/* 🔴 En línea, no justificado a los extremos, y sin separador antes del ahorro
						    fiscal: así lo deja la referencia. Justificar etiqueta y valor a los dos
						    lados de la tarjeta obliga a barrer la vista de un extremo a otro para
						    emparejar cada cifra con lo que significa. */}
						<div className="text-sm text-muted-foreground space-y-1 mt-3">
							<p>
								{translate('benefit.simulator.companyContributes')}{' '}
								<span className="text-foreground font-medium">
									{fmtEur(monthlyCompany)} {translate('benefit.simulator.perMonth')}
								</span>
							</p>
							<p>
								{translate('benefit.simulator.youContribute')}{' '}
								<span className="text-foreground font-medium">
									{isPercentageMode
										? `${fmtNum(safeSliderValue, 2)}% ${translate('benefit.simulator.ofSalary')}`
										: `${fmtNum(monthlyEurValue, 0)}€ ${translate('benefit.simulator.perMonth')}`}
								</span>
							</p>
							<p>
								{translate('benefit.simulator.estimatedTaxSavings')}{' '}
								<span className="text-primary font-medium">
									~{fmtNum(taxSaving, 0)}€ {translate('benefit.simulator.perYear')}
								</span>
							</p>
						</div>
						{planReturn && (
							<p className="text-xs text-muted-foreground mt-3">
								{translate('benefit.simulator.returnNote').replace(
									'{period}',
									periodWithRange(
										translate,
										planReturn.periodYears,
										periodRangeLabel(translate, planReturn.period)
									)
								)}
							</p>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
};

export default ProjectionSimulatorV2;
